<script>
  /**
   * Shared button. Variants:
   * - primary:   solid brand action
   * - secondary: white with border, brand text
   * - ghost:     borderless, brand text
   * - black:     deep brand (hero CTAs)
   * - outline:   white with border, neutral text
   * - danger:    destructive action
   * Extra props (onclick, aria-*, title, ...) are forwarded to the element.
   */
  let {
    variant = 'primary',
    type = 'button',
    disabled = false,
    icon = null,
    className = '',
    children,
    ...rest
  } = $props();
</script>

<button
  {type}
  class={`btn btn-${variant} ${className}`}
  {disabled}
  {...rest}
>
  {#if icon}
    <span class="btn-icon">{#if typeof icon === 'string'}{@html icon}{:else}{icon}{/if}</span>
  {/if}
  <span class="btn-content">{@render children?.()}</span>
</button>

<style>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 0.625rem;
  padding: 0.55rem 1rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s, border-color 0.15s;
  outline: none;
  border: 1px solid transparent;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.06);
  user-select: none;
}
.btn:focus-visible {
  box-shadow: 0 0 0 3px var(--color-brand-100);
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-primary {
  background: var(--color-brand-700);
  color: #fff;
}
.btn-primary:hover:not(:disabled),
.btn-primary:focus-visible:not(:disabled) {
  background: var(--color-brand-800);
}

.btn-secondary {
  background: #fff;
  color: var(--color-brand-700);
  border-color: #e5e7eb;
}
.btn-secondary:hover:not(:disabled),
.btn-secondary:focus-visible:not(:disabled) {
  background: var(--color-brand-50);
  border-color: var(--color-brand-600);
}

.btn-ghost {
  background: transparent;
  color: var(--color-brand-700);
  box-shadow: none;
}
.btn-ghost:hover:not(:disabled),
.btn-ghost:focus-visible:not(:disabled) {
  background: var(--color-brand-50);
}

.btn-black {
  background: var(--color-brand-900);
  color: #fff;
  border-color: var(--color-brand-900);
}
.btn-black:hover:not(:disabled),
.btn-black:focus-visible:not(:disabled) {
  background: var(--color-brand-950);
  border-color: var(--color-brand-950);
}

.btn-outline {
  background: #fff;
  color: #374151;
  border-color: #d1d5db;
}
.btn-outline:hover:not(:disabled),
.btn-outline:focus-visible:not(:disabled) {
  background: #f9fafb;
  color: #111827;
  border-color: #9ca3af;
}

.btn-danger {
  background: #dc2626;
  color: #fff;
}
.btn-danger:hover:not(:disabled),
.btn-danger:focus-visible:not(:disabled) {
  background: #b91c1c;
}

.btn-icon {
  display: flex;
  align-items: center;
  font-size: 1.1em;
}
.btn-content {
  display: flex;
  align-items: center;
}
</style>
